import { useEffect, useMemo, useState } from "react";
import type { GeneratedSignal } from "../api/dataSource";
import type { TradePreview } from "../types";
import { fmtPx, fmtUSD } from "../lib/format";

type Props = {
  signal: GeneratedSignal;
  onClose: () => void;
  onPreview: (
    signal: GeneratedSignal,
    riskDollars?: string,
  ) => Promise<{ preview: TradePreview; actionable: boolean; issues: string[] }>;
  onConfirm: (riskDollars: string) => Promise<string>;
};

export const SignalTradeTicketModal = ({
  signal,
  onClose,
  onPreview,
  onConfirm,
}: Props) => {
  const [riskDollars, setRiskDollars] = useState("100.00");
  const [preview, setPreview] = useState<TradePreview | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [actionable, setActionable] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const riskNumber = Number(riskDollars);
  const validRisk = Number.isFinite(riskNumber) && riskNumber > 0;
  const target = signal.targets[0];
  const assetClass = signal.assetClass ?? "equity";
  // Per-asset-class sizing rules (Phase 9-UI): a lookup instead of
  // the crypto/equity ternaries, so a new asset class is one entry
  // here — not another two-way branch to audit.
  //   quantityStep — the grid the derived qty floors to;
  //   minQuantity  — smallest orderable qty (min-risk basis).
  const sizingRules = useMemo((): {
    quantityStep: number;
    minQuantity: number;
    minSizeLabel: string;
  } => {
    if (assetClass === "crypto") {
      return {
        quantityStep: Number(
          signal.quantityStep ?? signal.minQuantity ?? 0.00000001,
        ),
        minQuantity: Math.max(
          Number(signal.quantityStep ?? 0.00000001),
          Number(signal.minQuantity ?? 0),
        ),
        minSizeLabel: "the minimum quantity",
      };
    }
    if (assetClass === "forex") {
      // Whole currency units as the v1 default. NOTE: tastyfx (the
      // Phase 9b broker) sizes in LOTS (100k units standard) — Phase 9c
      // strategies carry fx_pairs-derived steps; default 1 unit.
      return {
        quantityStep: Number(signal.quantityStep ?? 1),
        minQuantity: Math.max(
          Number(signal.quantityStep ?? 1),
          Number(signal.minQuantity ?? 1),
        ),
        minSizeLabel: "1 currency unit",
      };
    }
    return { quantityStep: 1, minQuantity: 1, minSizeLabel: "1 whole share" };
  }, [assetClass, signal.minQuantity, signal.quantityStep]);
  const localSizing = useMemo(() => {
    if (target === undefined) return null;
    const riskPerShare =
      signal.direction === "short"
        ? signal.stop - signal.entry
        : signal.entry - signal.stop;
    const gainPerShare =
      signal.direction === "short"
        ? signal.entry - target
        : target - signal.entry;
    if (riskPerShare <= 0 || gainPerShare <= 0) return null;
    return {
      riskPerShare,
      gainPerShare,
      quantityStep: sizingRules.quantityStep,
      minRiskDollars: riskPerShare * sizingRules.minQuantity,
    };
  }, [signal, sizingRules, target]);
  const localPreview = useMemo(() => {
    if (!validRisk || target === undefined || localSizing === null) return null;
    // Floor to the asset class's quantity grid. Equity's step of 1
    // reproduces the legacy whole-share flooring exactly.
    const qty =
      Math.floor(
        riskNumber / localSizing.riskPerShare / localSizing.quantityStep,
      ) * localSizing.quantityStep;
    if (qty <= 0) return null;
    const actualRiskDollars = qty * localSizing.riskPerShare;
    const targetGainDollars = qty * localSizing.gainPerShare;
    return {
      derivedQty: qty,
      actualRiskDollars,
      targetGainDollars,
      riskRewardRatio:
        actualRiskDollars > 0 ? targetGainDollars / actualRiskDollars : 0,
      basisPrice: signal.entry,
      stopPrice: signal.stop,
      targetPrice: target,
      riskPerShare: localSizing.riskPerShare,
      gainPerShare: localSizing.gainPerShare,
    };
  }, [assetClass, localSizing, riskNumber, signal.entry, signal.stop, target, validRisk]);
  const minRiskDollars = localSizing?.minRiskDollars ?? 0;
  const belowMinimum =
    validRisk &&
    minRiskDollars > 0 &&
    riskNumber < minRiskDollars;

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (!validRisk) {
        setPreview(null);
        setIssues(["Enter a positive dollar risk."]);
        setActionable(false);
        return;
      }
      setLoadingPreview(true);
      setError(null);
      void onPreview(signal, riskNumber.toFixed(2))
        .then((result) => {
          setPreview(result.preview);
          setIssues(result.issues);
          setActionable(result.actionable);
        })
        .catch((e) => {
          setPreview(localPreview);
          setIssues([
            e instanceof Error ? e.message : "Live preview failed",
          ]);
          setActionable(false);
          setError(e instanceof Error ? e.message : "Live preview failed");
        })
        .finally(() => setLoadingPreview(false));
    }, 250);
    return () => window.clearTimeout(id);
  }, [localPreview, onPreview, riskNumber, signal, validRisk]);

  const shownPreview = preview ?? localPreview;

  const submit = async () => {
    if (!validRisk || shownPreview === null || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(riskNumber.toFixed(2));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Approval queue failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="tp-modal-back"
      onClick={submitting ? undefined : onClose}
      role="presentation"
    >
      <div
        className="tp-modal tp-signal-ticket"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signal-ticket-title"
      >
        <div className="tp-modal-head">
          <div>
            <div className="eyebrow">Trade ticket</div>
            <div className="title" id="signal-ticket-title">
              <span className="ticker">{signal.symbol}</span> ·{" "}
              {signal.direction} ·{" "}
              <span className="num">{signal.strategyId}</span>
              {signal.companyName ? (
                <div className="tp-symbol-company">{signal.companyName}</div>
              ) : null}
            </div>
          </div>
          <div className="timer num">{loadingPreview ? "repricing" : "live preview"}</div>
        </div>
        <div className="tp-modal-body">
          <div className="tp-signal-ticket-grid">
            <div className="tp-kv">
              <div className="k">Entry:</div>
              <div className="v num">{fmtPx(signal.entry)}</div>
              <div className="k">Stop:</div>
              <div className="v num neg">{fmtPx(signal.stop)}</div>
              <div className="k">Target:</div>
              <div className="v num pos">
                {target === undefined ? "n/a" : fmtPx(target)}
              </div>
              <div className="k">Score:</div>
              <div className="v num">{signal.score}</div>
            </div>
            <div className="tp-signal-ticket-size">
              <label htmlFor="signal-ticket-risk">Dollar risk</label>
              <input
                id="signal-ticket-risk"
                className="tp-input"
                type="number"
                min="1"
                step="1"
                value={riskDollars}
                disabled={submitting}
                onChange={(e) => setRiskDollars(e.target.value)}
              />
              {shownPreview && (
                <div className="tp-ticket-preview">
                  <div>
                    <span>Qty</span>
                    <strong className="num">{shownPreview.derivedQty}</strong>
                  </div>
                  <div>
                    <span>If stop fills</span>
                    <strong className="num neg">
                      {fmtUSD(shownPreview.actualRiskDollars)}
                    </strong>
                  </div>
                  <div>
                    <span>If target fills</span>
                    <strong className="num pos">
                      {fmtUSD(shownPreview.targetGainDollars)}
                    </strong>
                  </div>
                  <div>
                    <span>R/R</span>
                    <strong className="num">
                      {shownPreview.riskRewardRatio.toFixed(2)}R
                    </strong>
                  </div>
                </div>
              )}
            </div>
          </div>
          {(issues.length > 0 || error) && (
            <div className="tp-ticket-issues" role="alert">
              {error && <div>{error}</div>}
              {issues.map((issue) => (
                <div key={issue}>{issue}</div>
              ))}
            </div>
          )}
          {belowMinimum && localSizing !== null && (
            <div className="tp-ticket-issues" role="status">
              Dollar risk must be at least{" "}
              <span className="num">{fmtUSD(localSizing.minRiskDollars)}</span>{" "}
              to size {sizingRules.minSizeLabel} at this stop distance.
            </div>
          )}
        </div>
        <div className="tp-modal-foot">
          <span className="num" style={{ color: "var(--tp-fg-3)", fontSize: 11 }}>
            Approval is queued only after this size is confirmed.
          </span>
          <div className="actions">
            <button
              type="button"
              className="tp-btn tp-btn-secondary"
              disabled={submitting}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-primary"
              disabled={
                submitting ||
                !validRisk ||
                shownPreview === null ||
                !actionable ||
                issues.length > 0
              }
              onClick={() => void submit()}
            >
              {submitting ? "Queueing..." : "Queue approval"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
